import { createContextScope } from '@radix-ui/react-context'
import React from 'react'
import type { ImageProps } from 'react-native'
import { Action } from './components/action'
import { BenefitTag } from './components/benefitTag'
import { Card } from './components/card'
import { Image } from './components/image'
import { Price } from './components/price'
import { Info } from './components/product-info'
import { InfoRating } from './components/rating'
import { Shipping } from './components/shipping'
import { TradeTag } from './components/trade-tag'
import { Validate } from './components/validate'
import { BenefitsPriceContainer } from './product-card.styles'
import type {
  ProductCardContext,
  ProductCardProps,
  ProductCardScopedProps,
} from './types'

export const DISPLAY_NAME: string = 'ProductCard'

const ProductCardRoot = (props: ProductCardScopedProps<ProductCardProps>) => {
  const { __scopeProductCard, children, onSale, withSponsoredTag, ...rest } =
    props

  type ProductCardComponent =
    | typeof ProductCard.Image
    | typeof ProductCard.Trade
    | typeof ProductCard.Info
    | typeof ProductCard.Rating
    | typeof ProductCard.Shipping
    | typeof ProductCard.Benefit
    | typeof ProductCard.Price
    | typeof ProductCard.Action
    | typeof ProductCard.Validate

  const allowedTypes = [
    ProductCard.Image,
    ProductCard.Trade,
    ProductCard.Info,
    ProductCard.Rating,
    ProductCard.Shipping,
    ProductCard.Benefit,
    ProductCard.Price,
    ProductCard.Action,
    ProductCard.Validate,
  ]

 
  const allChildren = React.Children.toArray(children).filter(child => {
    if (React.isValidElement(child)) {
      return allowedTypes.includes(child.type as ProductCardComponent)
    }
    return false
  })

  const primaryChildren = allChildren.filter(child => {
    if (!React.isValidElement(child)) return false

    if (
      child.type === ProductCard.Shipping &&
      child.props?.type === 'express'
    ) {
      return true
    }
    return ![
      ProductCard.Shipping,
      ProductCard.Benefit,
      ProductCard.Price,
      ProductCard.Action,
      ProductCard.Validate,
      // biome-ignore lint/suspicious/noExplicitAny:
    ].includes(child.type as any)
  })

  const extraChildren = allChildren.filter(child => {
    if (!React.isValidElement(child)) return false

    if (
      child.type === ProductCard.Shipping &&
      child.props?.type === 'express'
    ) {
      return false
    }
    return [
      ProductCard.Shipping,
      ProductCard.Benefit,
      ProductCard.Price,
      ProductCard.Action,
      ProductCard.Validate,
      // biome-ignore lint/suspicious/noExplicitAny:
    ].includes(child.type as any)
  })

  return (
    <ProductCardProvider
      scope={__scopeProductCard}
      onSale={onSale}
      withSponsoredTag={withSponsoredTag}
      {...rest}
    >
      <Card {...rest}>
        {primaryChildren}
        {extraChildren.length > 0 && (
          <BenefitsPriceContainer>{extraChildren}</BenefitsPriceContainer>
        )}
      </Card>
    </ProductCardProvider>
  )
}

const PublicImage = (props: ImageProps) => {
  return <Image {...props} />
}

ProductCardRoot.displayName = DISPLAY_NAME

/*
Scope Definition
*/

const [createProductCardContext] = createContextScope(DISPLAY_NAME)

export const [ProductCardProvider, useProductCardContext] =
  createProductCardContext<ProductCardContext>(DISPLAY_NAME)

export const ProductCard = {
  Root: ProductCardRoot,
  Image: PublicImage,
  Trade: TradeTag,
  Info: Info,
  Rating: InfoRating,
  Shipping: Shipping,
  Benefit: BenefitTag,
  Price: Price,
  Action: Action,
  Validate: Validate,
}

export type { ProductCardProps }
